/*
 * newsletter.js — drives the newsletter band's idle -> submitting -> success states.
 *
 * Shopify's customer form posts and reloads the whole page, which would throw
 * away the transition entirely: the spinner would flash for a frame and the
 * browser would navigate. So this intercepts the submit and posts with fetch,
 * letting the band swap in place.
 *
 * It is an enhancement, never a dependency. With no JS, no fetch, or a failed
 * request, the native post still runs and the section renders the success state
 * server-side from form.posted_successfully?. The only thing lost is the
 * animation.
 *
 * Reading the outcome is deliberately forgiving. The form posts to /contact, so
 * the reply is the contact page rather than this one, and the section's own
 * success marker is usually absent from it. A completed request with no form
 * error in the reply therefore counts as success — the only thing Shopify
 * rejects here is a malformed address, which the browser has already checked.
 *
 * Nothing in here ever calls form.submit(). An earlier version fell back to a
 * native post on timeout or network error, which reloaded the page and threw the
 * reader back to the top. A failure now keeps the typed address and says so.
 */
(function () {
  'use strict';

  // Long enough that the spinner reads as a state rather than a flicker, short
  // enough that a fast signup does not feel padded.
  var MIN_SUBMIT_MS = 500;
  // Past this we stop waiting rather than leave someone watching a spinner.
  var TIMEOUT_MS = 15000;

  function resetDelay(root) {
    var raw = parseInt(root.getAttribute('data-nl-reset') || '5', 10);
    return isNaN(raw) ? 5000 : raw * 1000;
  }

  // The confirmation is a message, not a destination: hand the band back so a
  // second person on the same device can sign up without reloading.
  function scheduleReset(root) {
    var delay = resetDelay(root);
    if (delay <= 0) return;
    setTimeout(function () {
      if (root.getAttribute('data-nl-state') !== 'success') return;
      var input = root.querySelector('[data-nl-input]');
      if (input) {
        input.removeAttribute('readonly');
        input.value = '';
      }
      clearError(root);
      setState(root, 'idle');
    }, delay);
  }

  function setState(root, state) {
    root.setAttribute('data-nl-state', state);
    if (state === 'success') scheduleReset(root);
  }

  function showError(root, message) {
    var el = root.querySelector('[data-nl-errmsg]');
    if (!el) return;
    el.textContent = message || 'Something went wrong. Please try again.';
    el.hidden = false;
  }

  function clearError(root) {
    var el = root.querySelector('[data-nl-errmsg]');
    if (!el) return;
    el.hidden = true;
  }

  // Only report an error the reply actually states. The section always renders
  // its error node (hidden when empty), so an empty one means "no error" — not
  // "unknown failure", which is how a successful signup ended up showing
  // "Something went wrong".
  function parseError(html) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector('[data-nl-errmsg]');
      var text = el && el.textContent ? el.textContent.trim() : '';
      if (text) return text;
      var shopify = doc.querySelector('.errors, .form__message--error');
      var alt = shopify && shopify.textContent ? shopify.textContent.trim() : '';
      return alt || null;
    } catch (e) {
      return null;
    }
  }

  function wire(root) {
    if (root.getAttribute('data-nl-wired') === 'true') return;
    root.setAttribute('data-nl-wired', 'true');

    var form = root.closest ? root.closest('form') : null;
    var input = root.querySelector('[data-nl-input]');
    var button = root.querySelector('[data-nl-submit]');
    if (!form || !input || !button) return;

    // Confirmed server-side (a normal post): still hand the band back after the
    // message has been read.
    if (root.getAttribute('data-nl-state') === 'success') scheduleReset(root);

    input.addEventListener('input', function () {
      if (root.getAttribute('data-nl-state') === 'error') {
        setState(root, 'idle');
        clearError(root);
      }
    });

    form.addEventListener('submit', function (event) {
      if (!window.fetch || !window.FormData || !window.DOMParser) return; // native post
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return; // let the browser prompt
      if (root.getAttribute('data-nl-state') === 'submitting') {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      clearError(root);
      setState(root, 'submitting');
      // Lock the value, per the motion spec, without disabling the field (a
      // disabled input is dropped from FormData).
      input.setAttribute('readonly', 'readonly');
      button.disabled = true;

      var started = Date.now();
      var settled = false;

      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        release();
        setState(root, 'error');
        showError(root, 'That took longer than expected. Please try again.');
      }, TIMEOUT_MS);

      function release() {
        clearTimeout(timer);
        input.removeAttribute('readonly');
        button.disabled = false;
      }

      function finish(ok, message) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        var wait = Math.max(0, MIN_SUBMIT_MS - (Date.now() - started));
        setTimeout(function () {
          if (ok) {
            button.disabled = false;
            setState(root, 'success');
          } else {
            release();
            setState(root, 'error');
            showError(root, message);
          }
        }, wait);
      }

      fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin'
      })
        .then(function (response) {
          var url = response.url || '';
          return response.text().then(function (html) {
            var confirmed =
              url.indexOf('customer_posted=true') !== -1 ||
              html.indexOf('data-nl-posted') !== -1;
            var error = confirmed ? null : parseError(html);
            // Treat "went through, nothing complained" as success. Requiring
            // positive proof failed every time, because the reply is the contact
            // page and carries neither the redirect flag nor our marker.
            var ok = confirmed || (response.ok && !error);
            finish(ok, error);
          });
        })
        .catch(function () {
          // Keep what they typed so a retry is one tap, and say what happened
          // instead of silently reloading the page.
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          release();
          setState(root, 'error');
          showError(root, 'We could not reach the server. Please try again.');
        });
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-nl-root]');
    for (var i = 0; i < roots.length; i++) wire(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // the theme editor re-renders a section without reloading the page
  document.addEventListener('shopify:section:load', init);
})();
